import { useState } from "react";
import { Column } from "@hightouch/htui";
import { Navigate, useNavigate } from "src/router";
import { AuthLayout } from "src/components/auth/auth-layout";
import { MicrosoftButton, EmailButton } from "src/components/auth/buttons";
import { CompanyForm } from "src/components/auth/company-form";
import { useAuthState } from "src/components/auth/use-auth-state";
import { useUser } from "src/contexts/user-context";
import { useSaveUserCompanyInfoMutation } from "src/graphql";
import { PartnerConnectHeader } from "src/pages/partner-connect";
import { PageSpinner } from "src/components/loading";
import GoogleIdentityButton from "src/components/auth/google-identity-button";
import { AuthCard } from "src/components/auth/auth-card";
import { OtherOptions } from "src/components/auth/other-options";
import { buildReturnToUrl } from "src/utils/auth-redirect";
import {
  isMcpAuthReturnTo,
  isValidApiRedirectOnLogin,
} from "src/components/auth/util";

export const Restricted = () => {
  const navigate = useNavigate();
  const { user, isCompanyRequired, refetch } = useUser();
  const {
    returnTo,
    partnerConnectionUUID,
    partnerInformation,
    microsoftAuthUrl,
    handleSuccessfulOauth,
    isHandlingSuccessfulOauth,
  } = useAuthState();
  const [isCompanyForm, setIsCompanyForm] = useState(false);

  const { mutateAsync: saveUserCompanyInfo } = useSaveUserCompanyInfoMutation();

  if (!isCompanyRequired) {
    return <Navigate to="/workspaces" />;
  }

  if (isCompanyForm) {
    return (
      <AuthLayout>
        <AuthCard heading="Tell us about your company">
          <CompanyForm
            onSubmit={async (data) => {
              await saveUserCompanyInfo({
                userId: String(user?.id),
                ...data,
              });
              await refetch();
              if (returnTo && isMcpAuthReturnTo(returnTo)) {
                if (isValidApiRedirectOnLogin(returnTo)) {
                  window.location.href = returnTo;
                } else {
                  navigate(returnTo);
                }
              } else {
                navigate("/welcome");
              }
            }}
          />
        </AuthCard>
      </AuthLayout>
    );
  }

  if (isHandlingSuccessfulOauth) {
    return <PageSpinner />;
  }

  return (
    <AuthLayout>
      <AuthCard heading="Please try again with a work email">
        {partnerInformation && (
          <PartnerConnectHeader partnerInformation={partnerInformation} />
        )}

        <Column gap={4}>
          <GoogleIdentityButton
            action="Sign up"
            onSuccess={handleSuccessfulOauth}
            partnerConnectionUUID={partnerConnectionUUID}
          />
          <MicrosoftButton
            action="Sign up"
            onClick={() => {
              window.location.href = microsoftAuthUrl;
            }}
          />
          <EmailButton
            action="Sign up"
            onClick={() => {
              navigate(
                returnTo
                  ? buildReturnToUrl("/login/email", returnTo)
                  : "/login/email",
              );
            }}
          />
        </Column>

        <OtherOptions variant="button" onClick={() => setIsCompanyForm(true)} />
      </AuthCard>
    </AuthLayout>
  );
};
